import axios from "axios";
import { unauthorizedInterceptors } from "../utilities/functions";

const settingsAPI = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/settings`,
  headers: {
    "Content-Type": "application/json",
  },
});
unauthorizedInterceptors(settingsAPI);

const setAuthHeader = () => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    settingsAPI.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
};

// Client API Functions
export const getAllClients = async () => {
  setAuthHeader();
  return await settingsAPI.get("api/clients/");
};

export const createClient = async (clientData) => {
  setAuthHeader();
  return await settingsAPI.post("api/clients/", clientData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const updateClient = async (clientId, clientData) => {
  setAuthHeader();
  return await settingsAPI.put(`api/clients/${clientId}/`, clientData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const getClient = async (id) => {
  setAuthHeader();
  return await settingsAPI.get(`api/clients/${id}/`);
};

export const deleteClient = async (clientId) => {
  setAuthHeader();
  return await settingsAPI.delete(`api/clients/${clientId}/`);
};

// Brand API Functions
export const getAllBrands = async () => {
  setAuthHeader();
  return await settingsAPI.get("api/brands/");
};

export const createBrand = async (brandData) => {
  setAuthHeader();
  return await settingsAPI.post("api/brands/", brandData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const updateBrand = async (brandId, brandData) => {
  setAuthHeader();
  return await settingsAPI.put(`api/brands/${brandId}/`, brandData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const getBrand = async (brandId) => {
  setAuthHeader();
  return await settingsAPI.get(`api/brands/${brandId}/`);
};

export const deleteBrand = async (brandId) => {
  setAuthHeader();
  return await settingsAPI.delete(`api/brands/${brandId}/`);
};

export const fetchCallCentersByBrand = async (brandId) => {
  return await settingsAPI.get(`/api/brands/${brandId}/call_centers/`);
};

// Call Center API Functions
export const getAllCallCenters = async () => {
  setAuthHeader();
  return await settingsAPI.get("api/call-centers/");
};

export const getCallCenter = async (callCenterId) => {
  setAuthHeader();
  return await settingsAPI.get(`api/call-centers/${callCenterId}/`);
};

export const createCallCenter = async (callCenterData) => {
  setAuthHeader();
  return await settingsAPI.post("api/call-centers/", callCenterData);
};

export const updateCallCenter = async (callCenterId, callCenterData) => {
  setAuthHeader();
  return await settingsAPI.put(
    `api/call-centers/${callCenterId}/`,
    callCenterData
  );
};

export const deleteCallCenter = async (callCenterId) => {
  setAuthHeader();
  return await settingsAPI.delete(`api/call-centers/${callCenterId}/`);
};

// Phone Number API Functions
export const getAllPhoneNumbers = async (clientIds = []) => {
  setAuthHeader();
  const params = new URLSearchParams();
  clientIds.forEach((id) => params.append("client_ids[]", id));

  const queryString = params.toString();
  const endpoint = queryString
    ? `api/phone-numbers-list/?${queryString}`
    : "api/phone-numbers-list/";

  return await settingsAPI.get(endpoint);
};

export const createPhoneNumber = async (phoneNumberData) => {
  setAuthHeader();
  return await settingsAPI.post("api/phone-numbers/", phoneNumberData);
};

export const getPhoneNumber = async (phoneNumberId) => {
  setAuthHeader();
  return await settingsAPI.get(`api/phone-numbers/${phoneNumberId}/`);
};

export const updatePhoneNumber = async (phoneNumberId, phoneNumberData) => {
  setAuthHeader();
  return await settingsAPI.put(
    `api/phone-numbers/${phoneNumberId}/`,
    phoneNumberData
  );
};

export const deletePhoneNumber = async (phoneNumberId) => {
  setAuthHeader();
  return await settingsAPI.delete(`api/phone-numbers/${phoneNumberId}/`);
};

export const fetchCallCentersByClient = async (clientId) => {
  return await settingsAPI.get(`/api/clients/${clientId}/call_centers/`);
};

export const fetchPhoneNumbersByCallCenter = async (callCenterId) => {
  return await settingsAPI.get(
    `/api/call-centers/${callCenterId}/phone_numbers/`
  );
};

export const updatePhoneNumberOffer = async (
  phoneNumberId,
  offerId,
  prevPhoneNumber
) => {
  setAuthHeader();
  const payload = {
    offer_id: offerId,
    prev_phone_number: prevPhoneNumber,
  };

  return await settingsAPI.patch(
    `api/phone-numbers/${phoneNumberId}/update_offer/`,
    payload
  );
};

// Supplier API Functions
export const getAllSuppliers = async (clientIds = []) => {
  setAuthHeader();
  const params = new URLSearchParams();
  clientIds.forEach((id) => params.append("client_ids[]", id));

  const queryString = params.toString();
  const endpoint = queryString
    ? `api/suppliers/?${queryString}`
    : "api/suppliers/";

  return await settingsAPI.get(endpoint);
};

export const getSupplier = async (supplierId) => {
  setAuthHeader();
  return await settingsAPI.get(`api/suppliers/${supplierId}/`);
};

export const createSupplier = async (supplierData) => {
  setAuthHeader();
  return await settingsAPI.post("api/suppliers/", supplierData);
};

export const updateSupplier = async (supplierId, supplierData) => {
  setAuthHeader();
  return await settingsAPI.put(`api/suppliers/${supplierId}/`, supplierData);
};

export const deleteSupplier = async (supplierId) => {
  setAuthHeader();
  return await settingsAPI.delete(`api/suppliers/${supplierId}/`);
};

export const getSuppliersByType = async (supplier_type, brand_id) => {
  setAuthHeader();
  const params = new URLSearchParams();
  params.append("brand_id", brand_id);

  const endpoint = `api/suppliers/?${params.toString()}`;

  return await settingsAPI.get(endpoint);
};

// Supplier Type API Functions
export const getAllSupplierTypes = async () => {
  setAuthHeader();
  return await settingsAPI.get("api/supplier-types/");
};

export const createSupplierType = async (supplierTypeData) => {
  setAuthHeader();
  return await settingsAPI.post("api/supplier-types/", supplierTypeData);
};

export const updateSupplierType = async (supplierTypeId, supplierTypeData) => {
  setAuthHeader();
  return await settingsAPI.put(
    `api/supplier-types/${supplierTypeId}/`,
    supplierTypeData
  );
};

export const deleteSupplierType = async (supplierTypeId) => {
  setAuthHeader();
  return await settingsAPI.delete(`api/supplier-types/${supplierTypeId}/`);
};

// Warehouse API Functions
export const getAllWarehouses = async (clientIds = []) => {
  setAuthHeader();
  const params = new URLSearchParams();
  clientIds.forEach((id) => params.append("client_ids[]", id));

  const queryString = params.toString();
  const endpoint = queryString
    ? `api/warehouses/?${queryString}`
    : "api/warehouses/";

  return await settingsAPI.get(endpoint);
};

export const getWarehouse = async (warehouseId) => {
  setAuthHeader();
  return await settingsAPI.get(`api/warehouses/${warehouseId}/`);
};

export const createWarehouse = async (warehouseData) => {
  setAuthHeader();
  return await settingsAPI.post("api/warehouses/", warehouseData);
};

export const updateWarehouse = async (warehouseId, warehouseData) => {
  setAuthHeader();
  return await settingsAPI.put(`api/warehouses/${warehouseId}/`, warehouseData);
};

export const deleteWarehouse = async (warehouseId) => {
  setAuthHeader();
  return await settingsAPI.delete(`api/warehouses/${warehouseId}/`);
};

// Status API Functions
export const getAllStatus = async () => {
  setAuthHeader();
  return await settingsAPI.get("api/status/");
};

// Warehouse Address API Functions
export const getWarehouseAddresses = async (brand_id) => {
  setAuthHeader();
  return await settingsAPI.get(`api/warehouse-addresses/?brand_id=${brand_id}`);
};

export const getWarehouseAddress = async (id) => {
  setAuthHeader();
  return await settingsAPI.get(`api/warehouse-addresses/${id}/`);
};

export const createWarehouseAddress = async (data) => {
  setAuthHeader();
  return await settingsAPI.post("api/warehouse-addresses/", data);
};

export const updateWarehouseAddress = async (id, data) => {
  setAuthHeader();
  return await settingsAPI.put(`api/warehouse-addresses/${id}/`, data);
};

export const deleteWarehouseAddress = async (id) => {
  setAuthHeader();
  return await settingsAPI.delete(`api/warehouse-addresses/${id}/`);
};

const brandClientCache = new Map();

const normalizeClientIds = (clientIds = []) => {
  if (!Array.isArray(clientIds)) {
    return {
      normalizedIds: [],
      cacheKey: "__none__",
    };
  }

  const normalizedIds = clientIds
    .map((id) => String(id).trim())
    .filter((id) => id.length > 0);

  if (normalizedIds.length === 0) {
    return {
      normalizedIds: [],
      cacheKey: "__none__",
    };
  }

  const uniqueSortedIds = Array.from(new Set(normalizedIds)).sort((a, b) =>
    a.localeCompare(b)
  );

  return {
    normalizedIds: uniqueSortedIds,
    cacheKey: uniqueSortedIds.join(","),
  };
};

export const fetchBrandsBySelectedClients = async (clientIds = []) => {
  setAuthHeader();

  const { normalizedIds, cacheKey } = normalizeClientIds(clientIds);

  // If no client IDs provided, avoid hitting the endpoint unnecessarily
  if (brandClientCache.has(cacheKey)) {
    return brandClientCache.get(cacheKey);
  }

  const params = new URLSearchParams();
  normalizedIds.forEach((id) => params.append("client_ids[]", id));
  const queryString = params.toString();

  const requestPromise = settingsAPI
    .get(`api/brands/filter-by-client/${queryString ? `?${queryString}` : ""}`)
    .catch((error) => {
      brandClientCache.delete(cacheKey);
      throw error;
    });

  brandClientCache.set(cacheKey, requestPromise);
  return requestPromise;
};

export const clearBrandsByClientCache = () => {
  brandClientCache.clear();
};

export const uploadEmail = (formData) => {
  return settingsAPI.post("/api/report-emails/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const getColumnState = (tableName) => {
  setAuthHeader();
  return settingsAPI.get(`api/column-states/${tableName}/`);
};

export const setColumnState = async (tableName, columnState) => {
  setAuthHeader();
  return await settingsAPI.post("api/column-states/", {
    table_name: tableName,
    column_state: columnState,
  });
};

export const getLoginHistory = async () => {
  setAuthHeader();
  return await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/auth/api/login-history/`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    }
  );
};

//Access Control API Functions
export const getAllUsers = async () => {
  setAuthHeader();
  return await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/auth/api/users/`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    }
  );
};

export const getUserById = async (userId) => {
  const response = await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/auth/api/users/${userId}/`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    }
  );
  return response.data;
};

export const createUser = async (userData) => {
  return await axios.post(
    `${process.env.REACT_APP_API_BASE_URL}/auth/api/register/`,
    userData,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    }
  );
};

export const updateUser = async (userId, data) => {
  return await axios.patch(
    `${process.env.REACT_APP_API_BASE_URL}/auth/api/users/${userId}/`,
    data,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    }
  );
};

export const deleteUser = async (userId) => {
  return await axios.delete(
    `${process.env.REACT_APP_API_BASE_URL}/auth/api/users/${userId}/`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    }
  );
};

//get current user
export const getCurrentUser = async () => {
  const accessToken =
    sessionStorage.getItem("accessToken") ||
    localStorage.getItem("accessToken");
  if (!accessToken) return null;

  // Make sure the slash is present
  const response = await axios.get(
    `${process.env.REACT_APP_API_BASE_URL}/auth/api/users/me/`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return response.data;
};
